const editors = new WeakMap();

export function normalizeMusicEventOptions(items) {
    if (!Array.isArray(items)) return [];
    const seen = new Set();
    return items.slice(0, 20).flatMap(item => {
        if (!item || typeof item.label !== 'string' || !item.label.trim()
            || !['string', 'number'].includes(typeof item.id)
            || (typeof item.id === 'number' && !Number.isSafeInteger(item.id))) return [];
        const id = String(item.id);
        if (!/^[1-9]\d{0,18}$/.test(id) || seen.has(id)) return [];
        seen.add(id);
        return [{ id, label: item.label.slice(0, 1000) }];
    });
}

function replaceOptions(select, items, preserve = true) {
    const current = select.value;
    const label = select.selectedOptions[0]?.textContent;
    const placeholder = select.options[0]?.textContent || '';
    select.replaceChildren(new Option(placeholder, ''));
    if (preserve && current && !items.some(item => item.id === current)) select.add(new Option(label || current, current));
    for (const item of items) select.add(new Option(item.label, item.id));
    select.value = preserve ? current : '';
}

function disposeEditor(form) {
    const state = editors.get(form);
    state?.events.abort();
    state?.controllers.forEach(controller => controller.abort());
    state?.timers.forEach(timer => clearTimeout(timer));
    editors.delete(form);
}

function initializeEditor(form) {
    const nodes = [...form.querySelectorAll('[data-billing-list], [data-add-artist], [data-lookup-query], [data-event-local]')];
    const previous = editors.get(form);
    if (previous && !previous.events.signal.aborted && previous.nodes.length === nodes.length && previous.nodes.every((node, index) => node === nodes[index])) return;
    disposeEditor(form);
    const state = { controllers: new Set(), timers: new Set(), events: new AbortController(), nodes };
    editors.set(form, state);
    const listen = (target, name, callback) => target?.addEventListener(name, callback, { signal: state.events.signal });
    const billing = form.querySelector('[data-billing-list]');
    const template = form.querySelector('[data-billing-template]');
    const rows = () => [...billing.querySelectorAll('[data-billing-row]')];
    const reindex = () => rows().forEach((row, index, list) => {
        const name = row.querySelector('[data-billing-name]').textContent;
        row.querySelector('[data-billing-id]').name = `artists[${index}][id]`;
        const role = row.querySelector('[data-billing-role]');
        role.name = `artists[${index}][role]`;
        role.setAttribute('aria-label', `Billing role for ${name}`);
        for (const action of ['up', 'down', 'remove']) {
            const button = row.querySelector(`[data-billing-action="${action}"]`);
            button.disabled = (action === 'up' && index === 0) || (action === 'down' && index === list.length - 1);
            button.setAttribute('aria-label', action === 'remove' ? `Remove ${name}` : `Move ${name} ${action}`);
        }
    });
    reindex();
    listen(form, 'submit', reindex);
    listen(billing, 'click', event => {
        const button = event.target.closest('[data-billing-action]');
        if (!button || button.disabled) return;
        const row = button.closest('[data-billing-row]');
        const action = button.dataset.billingAction;
        const focusTarget = row.nextElementSibling || row.previousElementSibling;
        if (action === 'up' && row.previousElementSibling) billing.insertBefore(row, row.previousElementSibling);
        if (action === 'down' && row.nextElementSibling) billing.insertBefore(row.nextElementSibling, row);
        if (action === 'remove') row.remove();
        reindex();
        if (action === 'remove') (focusTarget?.querySelector('[data-billing-role]') || form.querySelector('[data-add-artist]')).focus();
        else row.querySelector('[data-billing-role]').focus();
    });

    for (const lookup of form.querySelectorAll('[data-event-lookup]')) {
        const input = lookup.querySelector('[data-lookup-query]');
        const select = lookup.querySelector('[data-lookup-result]');
        const status = lookup.querySelector('[data-lookup-status]');
        let controller; let version = 0; let timer;
        const search = async () => {
            controller?.abort();
            controller = new AbortController();
            const request = controller;
            state.controllers.add(request);
            const current = ++version;
            const url = new URL(form.dataset.lookupUrl, location.origin);
            if (url.origin !== location.origin) return;
            url.searchParams.set('type', lookup.dataset.eventLookup);
            url.searchParams.set('query', input.value.trim());
            status.textContent = 'Searching...';
            try {
                const response = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin', signal: request.signal });
                if (!response.ok) throw new Error('Search unavailable');
                const data = await response.json();
                if (state.events.signal.aborted || !form.isConnected || current !== version) return;
                const items = normalizeMusicEventOptions(data.items);
                replaceOptions(select, items, lookup.dataset.eventLookup !== 'artist');
                status.textContent = items.length ? (data.has_more ? 'More matches available' : '') : 'No matches';
            } catch (error) {
                if (error.name !== 'AbortError' && form.isConnected && current === version) status.textContent = 'Search unavailable. Try again.';
            } finally {
                state.controllers.delete(request);
            }
        };
        listen(input, 'input', () => {
            clearTimeout(timer); state.timers.delete(timer);
            controller?.abort(); ++version;
            timer = setTimeout(() => { state.timers.delete(timer); search(); }, 300);
            state.timers.add(timer);
        });
        if (select.options.length <= 1) search();
        listen(lookup.querySelector('[data-add-artist]'), 'click', () => {
            if (!select.value) { select.focus(); return; }
            if (rows().some(row => row.querySelector('[data-billing-id]').value === select.value)) {
                status.textContent = 'Artist already selected.'; return;
            }
            if (rows().length >= 100) { status.textContent = 'Maximum 100 artists.'; return; }
            const row = template.content.firstElementChild.cloneNode(true);
            row.querySelector('[data-billing-id]').value = select.value;
            row.querySelector('[data-billing-name]').textContent = select.selectedOptions[0].textContent;
            row.querySelector('[data-billing-role]').value = rows().length ? 'supporting' : 'primary';
            billing.append(row); reindex();
            row.querySelector('[data-billing-role]').focus();
            status.textContent = 'Artist added.';
        });
    }

    const timezone = form.querySelector('[data-event-timezone]');
    for (const field of form.querySelectorAll('[data-event-time-field]')) {
        const local = field.querySelector('[data-event-local]');
        const select = field.querySelector('[data-event-offset]');
        const container = field.querySelector('[data-offset-container]');
        const status = field.querySelector('[data-time-status]');
        let controller; let version = 0;
        const update = async () => {
            controller?.abort(); ++version;
            const current = version;
            const selected = select.value;
            container.hidden = true; select.disabled = true;
            status.textContent = '';
            if (!local.value || !timezone.value) return;
            controller = new AbortController();
            const request = controller;
            state.controllers.add(request);
            const url = new URL(form.dataset.timeUrl, location.origin);
            if (url.origin !== location.origin) return;
            url.searchParams.set('local', local.value); url.searchParams.set('timezone', timezone.value);
            try {
                const response = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin', signal: request.signal });
                if (!response.ok) throw new Error('Time check unavailable');
                const data = await response.json();
                if (state.events.signal.aborted || !form.isConnected || current !== version) return;
                select.replaceChildren(new Option('Choose offset', ''));
                for (const option of Array.isArray(data.options) ? data.options.slice(0, 4) : []) {
                    if (typeof option.offset === 'string' && /^-?\d+$/.test(option.offset) && typeof option.label === 'string') select.add(new Option(option.label, option.offset));
                }
                const ambiguous = select.options.length > 2;
                container.hidden = !ambiguous; select.disabled = !ambiguous; select.required = ambiguous;
                select.value = selected;
                status.textContent = data.nonexistent ? 'This local time does not exist in this timezone.' : '';
            } catch (error) {
                if (error.name !== 'AbortError' && form.isConnected && current === version) status.textContent = 'Time check unavailable. Try again.';
            } finally {
                state.controllers.delete(request);
            }
        };
        listen(local, 'change', () => { select.value = ''; update(); });
        listen(timezone, 'change', () => { select.value = ''; update(); });
    }
}

export function initializeMusicEventEditors(root = document) {
    root.querySelectorAll('[data-music-event-editor]').forEach(initializeEditor);
}

if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeMusicEventEditors());
    else initializeMusicEventEditors();
    document.addEventListener('livewire:navigated', () => initializeMusicEventEditors());
    window.addEventListener('pageshow', () => initializeMusicEventEditors());
    document.addEventListener('focusin', event => {
        const form = event.target.closest?.('[data-music-event-editor]');
        if (form) initializeEditor(form);
    });
    document.addEventListener('livewire:navigating', () => {
        document.querySelectorAll('[data-music-event-editor]').forEach(disposeEditor);
    });
}
